// Clenzo add-on for Horizon. Only touches elements inside .clz
const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

/** Tell Horizon's cart icon / drawer that the cart changed (works across Horizon versions). */
async function notifyTheme(cart, variantId, quantity) {
  let notified = false;
  try {
    const { CartLinesUpdateEvent } = await import('@shopify/events');
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variantId), quantity }],
        promise: deferred.promise,
      })
    );
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, itemCount: quantity, source: 'clenzo', didError: false },
    });
    notified = true;
  } catch (e) { /* older Horizon */ }

  if (!notified) {
    try {
      const events = await import('@theme/events');
      if (events.CartAddEvent) {
        document.dispatchEvent(new events.CartAddEvent({}, 'clenzo', { itemCount: quantity, source: 'clenzo' }));
      }
    } catch (e) { /* ignore */ }
  }

  // Fallback: update any cart bubble directly
  document.querySelectorAll('[ref="cartBubbleCount"]').forEach((el) => {
    el.textContent = cart.item_count < 100 ? String(cart.item_count) : '';
    el.classList.toggle('hidden', cart.item_count === 0);
  });
  document.querySelectorAll('[ref="cartBubble"]').forEach((el) => el.classList.toggle('visually-hidden', cart.item_count === 0));
}

let toastTimer;
function toast(text) {
  const el = document.getElementById('clz-toast');
  if (!el) return;
  el.querySelector('.clz-toast__text').textContent = text;
  el.hidden = false;
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => { el.hidden = true; }, 3500);
}

/** Open Horizon's cart drawer (no message). Falls back to the cart page if there is no drawer. */
function openCartDrawer() {
  const drawer = document.querySelector('theme-drawer#cart-drawer');
  if (!drawer) { window.location.href = `${root}cart`; return; }
  const open = () => { if (!drawer.isOpen && typeof drawer.open === 'function') drawer.open(); };
  customElements.whenDefined('theme-drawer').then(() => requestAnimationFrame(open));
}

document.addEventListener('submit', async (e) => {
  const form = e.target;
  if (!(form instanceof HTMLFormElement) || !form.closest('.clz') || !form.action.includes('/cart/add')) return;
  if (form.hasAttribute('data-no-ajax')) return;
  e.preventDefault();
  const btn = form.querySelector('[type="submit"]');
  if (btn) { btn.disabled = true; btn.setAttribute('aria-busy', 'true'); }
  const data = new FormData(form);
  try {
    const res = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
      body: data,
    });
    const json = await res.json();
    if (!res.ok) { toast(json.description || 'Could not add to cart'); return; }
    const cart = await fetch(`${root}cart.js`).then((r) => r.json());
    await notifyTheme(cart, data.get('id'), Number(data.get('quantity') || 1));
    openCartDrawer();
  } catch (err) {
    form.setAttribute('data-no-ajax', '');
    form.submit();
  } finally {
    if (btn) { btn.disabled = false; btn.removeAttribute('aria-busy'); }
  }
});

if (document.querySelector('.clz-mobile-bar')) document.body.classList.add('clz-has-mobile-bar');

// Before / after comparison sliders
document.addEventListener('input', (e) => {
  const r = e.target;
  if (!(r instanceof HTMLInputElement) || !r.classList.contains('clz-compare__range')) return;
  r.closest('[data-clz-compare]').style.setProperty('--pos', `${r.value}%`);
});
if ('IntersectionObserver' in window && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      io.unobserve(entry.target);
      const el = entry.target;
      const range = el.querySelector('.clz-compare__range');
      const keys = [50, 30, 70, 50];
      const seg = 550;
      let t0;
      const step = (now) => {
        if (el.dataset.touched) return;
        t0 = t0 || now;
        const t = (now - t0) / seg;
        const i = Math.min(Math.floor(t), keys.length - 2);
        const k = Math.min(t - i, 1);
        const ease = k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2;
        const v = keys[i] + (keys[i + 1] - keys[i]) * ease;
        el.style.setProperty('--pos', `${v}%`);
        if (range) range.value = Math.round(v);
        if (t < keys.length - 1) requestAnimationFrame(step);
      };
      setTimeout(() => requestAnimationFrame(step), 300);
      el.addEventListener('pointerdown', () => { el.dataset.touched = '1'; }, { once: true });
    });
  }, { threshold: 0.6 });
  document.querySelectorAll('[data-clz-compare]').forEach((el) => io.observe(el));
}

// Reveal-on-scroll for Clenzo sections
document.documentElement.classList.add('clz-js');
(() => {
  const els = document.querySelectorAll('[data-clz-reveal]');
  if (!('IntersectionObserver' in window)) { els.forEach((el) => el.classList.add('is-in')); return; }
  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) { entry.target.classList.add('is-in'); io.unobserve(entry.target); }
    });
  }, { threshold: 0.2 });
  els.forEach((el) => io.observe(el));
  // Safety net: never leave content hidden
  setTimeout(() => els.forEach((el) => el.classList.add('is-in')), 4000);
})();

// Product page: gallery, quantity, variants, sticky bar
document.querySelectorAll('[data-clz-product]').forEach((root) => {
  const track = root.querySelector('[data-clz-track]');
  const thumbs = [...root.querySelectorAll('[data-clz-thumb]')];
  const current = root.querySelector('[data-clz-current]');
  if (track) {
    const slides = [...track.children];
    thumbs.forEach((t) => t.addEventListener('click', () => {
      const s = slides[Number(t.dataset.clzThumb)];
      if (s) track.scrollTo({ left: s.offsetLeft - track.offsetLeft, behavior: 'smooth' });
    }));
    const go = (i) => {
      const s = slides[Math.max(0, Math.min(slides.length - 1, i))];
      if (s) track.scrollTo({ left: s.offsetLeft - track.offsetLeft, behavior: 'smooth' });
    };
    root.querySelectorAll('[data-clz-nav]').forEach((b) => b.addEventListener('click', () => {
      const i = Math.round(track.scrollLeft / track.clientWidth) + Number(b.dataset.clzNav);
      go(i >= slides.length ? 0 : i < 0 ? slides.length - 1 : i);
    }));
    const strip = root.querySelector('.clz-pd__thumbs');
    let raf;
    track.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const i = Math.round(track.scrollLeft / track.clientWidth);
        if (current) current.textContent = String(i + 1);
        thumbs.forEach((t, n) => t.setAttribute('aria-current', n === i ? 'true' : 'false'));
        // videos in the gallery: play the one in view (muted), pause the rest
        slides.forEach((sl, n) => {
          const vid = sl.querySelector('video');
          if (vid) { if (n === i) vid.play().catch(() => {}); else vid.pause(); }
          const ifr = sl.querySelector('iframe');
          if (ifr && n !== i) { try { ifr.contentWindow.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), '*'); ifr.contentWindow.postMessage(JSON.stringify({ method: 'pause' }), '*'); } catch (e) { /* ignore */ } }
        });
        const on = thumbs[i];
        if (strip && on) {
          const li = on.parentElement;
          const target = li.offsetLeft - (strip.clientWidth - li.offsetWidth) / 2;
          strip.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
        }
      });
    }, { passive: true });
  }
  const total = root.querySelector('[data-clz-total]');
  const qtyInput = root.querySelector('.clz-pd__qty input');
  const priceEl = root.querySelector('[data-clz-price]');
  const prefix = priceEl ? (priceEl.textContent.trim().match(/^[^\d]*/) || [''])[0] : '';
  const fmt = (cents) => prefix + (cents / 100).toLocaleString('en-PK', { maximumFractionDigits: 2 });
  const updateTotal = () => {
    if (!total || !qtyInput) return;
    const q = Math.max(1, parseInt(qtyInput.value, 10) || 1);
    total.hidden = q < 2;
    total.innerHTML = `Total for ${q}: <strong>${fmt(Number(total.dataset.unit) * q)}</strong>`;
  };
  root.querySelectorAll('[data-clz-qty]').forEach((b) => b.addEventListener('click', () => {
    const input = b.parentElement.querySelector('input');
    input.value = Math.max(1, (parseInt(input.value, 10) || 1) + Number(b.dataset.clzQty));
    updateTotal();
  }));
  if (qtyInput) qtyInput.addEventListener('input', updateTotal);
  const sel = root.querySelector('[data-clz-variant]');
  if (sel) sel.addEventListener('change', () => {
    const o = sel.options[sel.selectedIndex];
    const price = root.querySelector('[data-clz-price]');
    if (price) price.textContent = o.dataset.price;
    if (total) { total.dataset.unit = o.dataset.cents; updateTotal(); }
    const btn = root.querySelector('[data-clz-add]');
    const ok = o.dataset.available === 'true';
    if (btn) { btn.disabled = !ok; btn.querySelector('span').textContent = ok ? 'Add to cart' : 'Sold out'; }
  });
  const sticky = root.querySelector('[data-clz-sticky]');
  const addBtn = root.querySelector('[data-clz-add]');
  if (sticky && addBtn) {
    let ticking = false;
    const check = () => {
      ticking = false;
      const show = addBtn.getBoundingClientRect().bottom < 0;
      if (sticky.classList.contains('is-visible') === show) return;
      sticky.classList.toggle('is-visible', show);
      sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
      sticky.querySelector('button').tabIndex = show ? 0 : -1;
    };
    window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(check); } }, { passive: true });
    check();
    sticky.querySelector('[data-clz-sticky-add]').addEventListener('click', () => addBtn.click());
  }
});

// Policy pages: numbered headings + table of contents
document.querySelectorAll('[data-clz-policy]').forEach((root) => {
  const content = root.querySelector('[data-clz-policy-content]');
  const list = root.querySelector('[data-clz-toc-list]');
  const toc = root.querySelector('[data-clz-toc]');
  if (!content) return;
  content.querySelectorAll('h1').forEach((h) => h.remove());
  const heads = [...content.querySelectorAll('h2')];
  heads.forEach((h, i) => {
    const text = h.textContent.trim();
    const m = text.match(/^(\d+)[.)]\s*(.*)$/);
    const num = m ? m[1] : String(i + 1);
    const label = m ? m[2] : text;
    if (!h.id) h.id = 'section-' + (label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || i + 1);
    h.innerHTML = `<span class="clz-pol__num">${num}</span><span>${label}</span>`;
    if (list) {
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = '#' + h.id; a.textContent = label; a.dataset.target = h.id;
      li.appendChild(a); list.appendChild(li);
    }
  });
  if (toc && heads.length > 2) toc.hidden = false;
  if (window.matchMedia('(max-width: 989px)').matches) { const d = toc && toc.querySelector('details'); if (d) d.open = false; }
  if ('IntersectionObserver' in window && list) {
    const links = [...list.querySelectorAll('a')];
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        links.forEach((a) => a.classList.toggle('is-active', a.dataset.target === e.target.id));
      });
    }, { rootMargin: '-20% 0px -70% 0px' });
    heads.forEach((h) => io.observe(h));
  }
});

// ---------- Announcement: rotate messages on phones ----------
document.querySelectorAll('[data-clz-ann]').forEach((ann) => {
  const msgs = [...ann.querySelectorAll('.clz-ann__msg')];
  if (msgs.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  let i = 0;
  setInterval(() => {
    if (!window.matchMedia('(max-width: 749px)').matches) return;
    msgs[i].classList.remove('is-active');
    i = (i + 1) % msgs.length;
    msgs[i].classList.add('is-active');
  }, 3500);
});

const clzCfg = window.clzConfig || {};
const svg = (d, w = 18) => `<svg width="${w}" height="${w}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${d}</svg>`;
const ICON = {
  wa: '<path d="M4 20l1.3-3.9A8 8 0 1 1 8 19z"/><path d="M9.5 9.5c0 3 2 5 5 5l1-1.5-2-1-1 .8c-.8-.4-1.5-1.1-1.9-1.9l.8-1-1-2z"/>',
  truck: '<path d="M3 7h11v9H3z"/><path d="M14 10h4l3 3v3h-7"/><circle cx="7" cy="18" r="2"/><circle cx="17" cy="18" r="2"/>',
  check: '<path d="M5 12l5 5 9-10"/>',
  phone: '<path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2"/>',
  clock: '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>',
  gift: '<rect x="3" y="8" width="18" height="13" rx="2"/><path d="M12 8v13M3 12h18M12 8c-2-4-6-4-6-1s6 1 6 1 6 2 6-1-4-3-6 1"/>'
};

// ---------- Mobile menu: WhatsApp + contact block ----------
if (clzCfg.drawerExtras !== false) {
  const addExtras = () => {
    document.querySelectorAll('.menu-drawer').forEach((drawer) => {
      if (drawer.querySelector('.clz-drawer-extras')) return;
      const box = document.createElement('div');
      box.className = 'clz-drawer-extras';
      const wa = clzCfg.whatsapp ? `<a class="clz-drawer-extras__cta clz-drawer-extras__cta--wa" href="https://wa.me/${clzCfg.whatsapp}" target="_blank" rel="noopener">${svg(ICON.wa, 20)} Order on WhatsApp</a>` : '';
      const bundles = clzCfg.bundlesUrl ? `<a class="clz-drawer-extras__cta clz-drawer-extras__cta--bundles" href="${clzCfg.bundlesUrl}">${svg(ICON.gift, 20)} Shop bundles · free delivery</a>` : '';
      const tel = (clzCfg.phone || '').replace(/[^\d+]/g, '');
      box.innerHTML = `${wa}${bundles}<div class="clz-drawer-extras__info">
        ${clzCfg.phone ? `<span class="clz-drawer-extras__row">${svg(ICON.phone)}<a href="tel:${tel}">${clzCfg.phone}</a></span>` : ''}
        ${clzCfg.hours ? `<span class="clz-drawer-extras__row">${svg(ICON.clock)}${clzCfg.hours}</span>` : ''}
        <span class="clz-drawer-extras__row">${svg(ICON.check)}Cash on delivery across Pakistan</span></div>`;
      const nav = drawer.querySelector('.menu-drawer__navigation');
      if (nav) nav.insertAdjacentElement('afterend', box); else drawer.appendChild(box);
    });
  };
  addExtras();
  document.addEventListener('click', (e) => { if (e.target.closest('header-drawer summary')) setTimeout(addExtras, 50); });
}

// ---------- Cart drawer: free-delivery bar ----------
if (clzCfg.cartPerks !== false) {
  const handles = new Set(clzCfg.bundleHandles || []);
  const render = (drawer) => {
    const content = drawer.querySelector('.cart-drawer__content') || drawer.querySelector('.cart-drawer__inner');
    const items = [...drawer.querySelectorAll('.cart-items__title')];
    let bar = drawer.querySelector('.clz-cartbar');
    if (!content || items.length === 0) { if (bar) bar.remove(); return; }
    const hasBundle = items.some((a) => {
      const m = (a.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
      return m && handles.has(decodeURIComponent(m[1]));
    });
    const html = hasBundle
      ? `${svg(ICON.check, 20)}<span><strong>Free delivery unlocked!</strong> Your bundle ships free.</span>`
      : `${svg(ICON.truck, 20)}<span>Add a bundle to get <strong>free delivery</strong>. <a href="${clzCfg.bundlesUrl || '/collections/bundles'}">See bundles</a></span>`;
    // "Free delivery" tag under every bundle in the cart
    items.forEach((a) => {
      const m = (a.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
      const isB = m && handles.has(decodeURIComponent(m[1]));
      const next = a.nextElementSibling;
      const has = next && next.classList.contains('clz-cart-free');
      if (isB && !has) a.insertAdjacentHTML('afterend', `<span class="clz clz-cart-free"><span class="clz-free clz-free--mini">${svg(ICON.truck, 13)}<span>Free delivery</span></span></span>`);
      if (!isB && has) next.remove();
    });
    const cls = 'clz-cartbar ' + (hasBundle ? 'clz-cartbar--done' : 'clz-cartbar--todo');
    const state = hasBundle ? 'done' : 'todo';
    if (bar && bar.dataset.state === state && bar.parentElement === content) return;
    if (!bar) bar = document.createElement('div');
    bar.dataset.state = state;
    bar.className = cls;
    bar.innerHTML = html;
    if (bar.parentElement !== content) content.prepend(bar);
  };
  const watch = (drawer) => {
    let pending = false;
    const run = () => { pending = false; render(drawer); };
    new MutationObserver(() => { if (!pending) { pending = true; requestAnimationFrame(run); } }).observe(drawer, { childList: true, subtree: true });
    render(drawer);
  };
  const init = () => document.querySelectorAll('cart-drawer-component').forEach((d) => { if (!d.dataset.clzWatch) { d.dataset.clzWatch = '1'; watch(d); } });
  init();
  if (!document.querySelector('cart-drawer-component')) document.addEventListener('DOMContentLoaded', init);
}

// ---------- Star rating link: smooth-scroll to the Judge.me review widget ----------
document.addEventListener('click', (e) => {
  const a = e.target.closest('[data-clz-reviews-link]');
  if (!a) return;
  const target = document.querySelector('#judgeme_product_reviews, .jdgm-review-widget, .jdgm-widget.jdgm-review-widget');
  if (!target) return;
  e.preventDefault();
  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
});

// ---------- Product video: full popup on open (phones), customer closes it to see the product ----------
document.querySelectorAll('[data-clz-vid]').forEach((root) => {
  const bubble = root.querySelector('[data-clz-vid-bubble]');
  const modal = root.querySelector('[data-clz-vid-modal]');
  const main = root.querySelector('.clz-vid__main');
  const preview = root.querySelector('.clz-vid__preview');
  const bar = root.querySelector('[data-clz-vid-bar]');
  const soundBtn = root.querySelector('[data-clz-vid-sound]');
  if (!bubble || !modal || !main) return;
  document.body.appendChild(root); // keep it above page sections
  const isMobile = window.matchMedia('(max-width: 749px)').matches;
  if ((isMobile && root.dataset.mobile === 'false') || (!isMobile && root.dataset.desktop !== 'true')) return; // desktop only when switched on
  const key = root.dataset.key;
  const store = (k, v) => { try { if (v === undefined) return sessionStorage.getItem(k); sessionStorage.setItem(k, v); } catch (e) { return null; } return null; };
  const useBubble = root.dataset.bubble === 'true';
  if (preview) { preview.muted = true; preview.pause(); }

  let lastFocus = null;
  const sync = () => {
    root.classList.toggle('is-paused', main.paused);
    root.classList.toggle('is-muted', main.muted);
    if (soundBtn) soundBtn.hidden = !main.muted;
  };
  const showBubble = () => {
    if (!useBubble || store(key + '-x') === '1') return;
    bubble.hidden = false;
    if (preview) preview.play().catch(() => {});
  };
  const open = (withSound) => {
    lastFocus = document.activeElement;
    modal.hidden = false;
    bubble.hidden = true;
    if (preview) preview.pause();
    document.documentElement.classList.add('clz-vid-lock');
    main.currentTime = 0;
    main.muted = !withSound;
    main.play().catch(() => { main.muted = true; main.play().catch(() => {}); }).finally(sync);
    sync();
    root.querySelector('.clz-vid__close').focus({ preventScroll: true });
  };
  const close = () => {
    main.pause();
    modal.hidden = true;
    document.documentElement.classList.remove('clz-vid-lock');
    showBubble();
    if (lastFocus && lastFocus !== document.body) lastFocus.focus({ preventScroll: true });
  };
  root.querySelector('[data-clz-vid-open]').addEventListener('click', () => open(true));
  root.querySelectorAll('[data-clz-vid-close]').forEach((b) => b.addEventListener('click', close));
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !modal.hidden) close(); });
  root.querySelector('[data-clz-vid-dismiss]').addEventListener('click', () => {
    bubble.hidden = true;
    if (preview) preview.pause();
    store(key + '-x', '1');
  });
  root.querySelector('[data-clz-vid-toggle]').addEventListener('click', () => { if (main.paused) main.play().catch(() => {}); else main.pause(); });
  root.querySelector('[data-clz-vid-mute]').addEventListener('click', () => { main.muted = !main.muted; sync(); });
  if (soundBtn) soundBtn.addEventListener('click', () => { main.muted = false; main.play().catch(() => {}); sync(); });
  main.addEventListener('play', sync);
  main.addEventListener('pause', sync);
  main.addEventListener('volumechange', sync);
  main.addEventListener('click', () => {
    if (main.muted) { main.muted = false; sync(); return; }
    if (main.paused) main.play().catch(() => {}); else main.pause();
  });
  main.addEventListener('timeupdate', () => { if (bar && main.duration) bar.style.width = `${(main.currentTime / main.duration) * 100}%`; });
  // Add to cart from the popup: close it so the cart drawer is visible
  const form = root.querySelector('.clz-vid__form');
  if (form) form.addEventListener('submit', () => setTimeout(close, 0));

  const delay = Number(root.dataset.delay || 0);
  if (root.dataset.auto === 'true' && !(root.dataset.once === 'true' && store(key) === '1')) {
    setTimeout(() => { store(key, '1'); open(false); }, delay); // browsers only autoplay muted; "Tap for sound" turns it on
  } else {
    setTimeout(showBubble, delay);
  }
});

// Start decorative looping animations only after the page has loaded (keeps first paint fast)
(() => {
  const go = () => setTimeout(() => document.documentElement.classList.add('clz-loaded'), 600);
  if (document.readyState === 'complete') go(); else window.addEventListener('load', go, { once: true });
})();

// ---------- Instafeed: load the app only when its feed is close to the screen ----------
if (window.clzInstafeedHeld) {
  const feed = document.querySelector('#insta-feed, [id^="insta-feed"], .clz-insta');
  const load = () => {
    if (window.clzInstafeedLoaded) return;
    window.clzInstafeedLoaded = true;
    const src = window.instafeedScriptSrc || window.clzInstafeedHeld[0];
    if (!src) return;
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    document.body.appendChild(s);
  };
  if (feed && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { io.disconnect(); load(); }
    }, { rootMargin: '800px 0px' });
    io.observe(feed);
  } else if (feed) {
    window.addEventListener('scroll', load, { once: true, passive: true });
  }
}
